import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Clock, MapPin, BookOpen, ChevronLeft, Filter, AlertCircle, CheckCircle, PlayCircle, Loader2, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { examService } from "../../services/examService";
import { studentService } from "../../services/studentService";
import toast from "react-hot-toast";
import { schoolService } from "../../services/schoolService";
import { AdmitCardTemplate } from "../../Components/Common/AdmitCardTemplate";
import html2pdf from "html2pdf.js";

const ExamSchedule = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [fetchingSchedule, setFetchingSchedule] = useState(false);

    const [student, setStudent] = useState(null);
    const [academicSessions, setAcademicSessions] = useState([]);
    const [currentSessionId, setCurrentSessionId] = useState("");
    const [examTypes, setExamTypes] = useState([]);
    const [selectedExamTypeId, setSelectedExamTypeId] = useState("");
    const [schedules, setSchedules] = useState([]);
    const [schoolInfo, setSchoolInfo] = useState(null);
    const admitCardRef = React.useRef(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const studentId = localStorage.getItem("selectedStudentId");
            if (!studentId) {
                toast.error("No student selected");
                navigate("/dashboard");
                return;
            }

            const enrollmentRes = await studentService.getStudentEnrollment(studentId);
            const enrollments = Array.isArray(enrollmentRes.data) ? enrollmentRes.data : [enrollmentRes.data];
            const activeEnrollment = enrollments.find(e => e.active === true && e.status === 'Enrolled') || enrollments[0];

            if (!activeEnrollment) {
                toast.error("Enrollment data not found");
                return;
            }
            setStudent(activeEnrollment);

            // 2. Fetch Academic Sessions and detect current
            const sessionsRes = await examService.getAcademicSessions();
            const sessions = sessionsRes.data?.data || [];
            setAcademicSessions(sessions);

            const currentSession = sessions.find(s => s.current === true);
            if (currentSession) {
                setCurrentSessionId(currentSession._id);
            }

            // 3. Fetch Exam Types
            const examTypesRes = await examService.getExamTypes();
            setExamTypes(examTypesRes.data?.data || []);

            // 4. Fetch School Info
            const schoolRes = await schoolService.getSchoolDetails();
            if (schoolRes.success && schoolRes.data && schoolRes.data.length > 0) {
                const school = schoolRes.data[0];
                setSchoolInfo({
                    ...school,
                    logo: school.logoUrl, // Map logoUrl to logo as expected by template
                    signatureUrl: school.signatureUrl
                });
            }

        } catch (error) {
            console.error("Error fetching initial data:", error);
            toast.error("Failed to load initial data");
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const fetchSchedule = useCallback(async (examTypeId) => {
        if (!examTypeId || !currentSessionId || !student?.class?._id) return;

        try {
            setFetchingSchedule(true);
            const response = await examService.getExamSchedule(
                currentSessionId,
                student.class._id,
                examTypeId
            );
            setSchedules(response.data?.schedules || []);
        } catch (error) {
            console.error("Error fetching exam schedule:", error);
            toast.error("Failed to load exam schedule");
        } finally {
            setFetchingSchedule(false);
        }
    }, [currentSessionId, student]);

    useEffect(() => {
        if (selectedExamTypeId) {
            fetchSchedule(selectedExamTypeId);
        }
    }, [selectedExamTypeId, fetchSchedule]);

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'ongoing':
                return (
                    <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                        <PlayCircle size={12} />
                        Ongoing
                    </span>
                );
            case 'completed':
                return (
                    <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
                        <CheckCircle size={12} />
                        Completed
                    </span>
                );
            default:
                return (
                    <span className="flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                        Upcoming
                    </span>
                );
        }
    };

    const handleDownloadAdmitCard = () => {
        if (!selectedExamTypeId) {
            toast.error("Please select an exam type first");
            return;
        }
        if (!schedules || schedules.length === 0) {
            toast.error("No schedule available for selected exam type");
            return;
        }

        const element = admitCardRef.current;
        const opt = {
            margin: 0,
            filename: `AdmitCard_${student?.student?.firstName || 'Student'}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2, useCORS: true, logging: true },
            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save();
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="flex flex-col items-center">
                    <Loader2 className="w-10 h-10 text-pragyaBlue animate-spin mb-4" />
                    <p className="text-slate-500 font-medium">Loading Examination Portal...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50/50 pb-32">
            {/* Header */}
            <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
                <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between">
                    <button
                        onClick={() => navigate(-1)}
                        className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                    >
                        <ChevronLeft size={24} className="text-slate-600" />
                    </button>
                    <h1 className="text-lg font-bold text-slate-800">Exam Schedule</h1>
                    <button
                        onClick={handleDownloadAdmitCard}
                        disabled={!selectedExamTypeId || schedules.length === 0}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors text-sm font-medium shadow-md hover:shadow-lg ${!selectedExamTypeId || schedules.length === 0
                            ? "bg-gray-400 cursor-not-allowed text-gray-200"
                            : "bg-green-600 text-white hover:bg-green-700"
                            }`}
                    >
                        <FileText size={18} />
                        <span className="hidden sm:inline">Admit Card</span>
                    </button>
                </div>
            </header>

            <main className="max-w-5xl mx-auto px-4 py-6">
                {/* Student Info Summary */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-pragyaBlue text-white p-5 rounded-3xl shadow-lg shadow-pragyaBlue/20 mb-6 flex items-center justify-between"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md">
                            <BookOpen className="text-white" size={24} />
                        </div>
                        <div>
                            <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Exam Board</p>
                            <h2 className="text-lg font-bold">{student?.class?.name} - {student?.section}</h2>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-white/70 text-xs font-medium uppercase tracking-wider">Session</p>
                        <p className="font-bold text-sm">
                            {academicSessions.find(s => s._id === currentSessionId)?.label || "N/A"}
                        </p>
                    </div>
                </motion.div>

                {/* Filters */}
                <div className="mb-6">
                    <div className="relative group">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Filter size={18} className="text-slate-400" />
                        </div>
                        <select
                            value={selectedExamTypeId}
                            onChange={(e) => setSelectedExamTypeId(e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-pragyaBlue/20 focus:border-pragyaBlue transition-all appearance-none text-slate-700 font-medium"
                        >
                            <option value="">Select Exam Type</option>
                            {examTypes.map(type => (
                                <option key={type._id} value={type._id}>{type.name}</option>
                            ))}
                        </select>
                        <div className="absolute inset-y-0 right-4 flex items-center pointer-events-none">
                            <motion.div
                                animate={{ y: [0, 2, 0] }}
                                transition={{ repeat: Infinity, duration: 2 }}
                            >
                                <ChevronLeft size={16} className="text-slate-400 transform -rotate-90" />
                            </motion.div>
                        </div>
                    </div>
                </div>

                {/* Schedule Content */}
                <AnimatePresence mode="wait">
                    {fetchingSchedule ? (
                        <motion.div
                            key="loading-schedule"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="flex flex-col items-center py-20"
                        >
                            <Loader2 className="w-8 h-8 text-pragyaBlue animate-spin mb-4" />
                            <p className="text-slate-500 text-sm">Fetching timetable...</p>
                        </motion.div>
                    ) : !selectedExamTypeId ? (
                        <motion.div
                            key="no-selection"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-300"
                        >
                            <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Filter size={32} className="text-slate-300" />
                            </div>
                            <h3 className="text-slate-700 font-bold text-lg mb-1">Select Exam Type</h3>
                            <p className="text-slate-500 text-sm">Please select an exam type to view the schedule.</p>
                        </motion.div>
                    ) : schedules.length === 0 ? (
                        <motion.div
                            key="empty-state"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="text-center py-16 bg-white rounded-3xl border border-dashed border-slate-300"
                        >
                            <div className="w-16 h-16 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4">
                                <AlertCircle size={32} className="text-orange-400" />
                            </div>
                            <h3 className="text-slate-700 font-bold text-lg mb-1">No Schedule Published</h3>
                            <p className="text-slate-500 text-sm max-w-xs mx-auto">No exam schedule has been published yet for this session.</p>
                        </motion.div>
                    ) : (
                        <motion.div
                            key="schedule-list"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="space-y-4"
                        >
                            <div className="hidden md:grid md:grid-cols-4 px-6 mb-2 text-xs font-bold text-slate-400 uppercase tracking-widest">
                                <span>Date & Subject</span>
                                <span>Time</span>
                                <span>Room</span>
                                <span>Status</span>
                            </div>

                            {schedules.map((item, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className="bg-white p-5 md:p-6 rounded-3xl border border-slate-100 shadow-sm hover:shadow-md transition-all hover:border-pragyaBlue/20"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                                        {/* Date & Subject */}
                                        <div className="flex items-center gap-4">
                                            <div className="flex flex-col items-center justify-center w-12 h-12 bg-pragyaBlue/10 rounded-2xl text-pragyaBlue">
                                                <span className="text-[10px] font-bold uppercase leading-none">
                                                    {new Date(item.examDate).toLocaleString('default', { month: 'short' })}
                                                </span>
                                                <span className="text-lg font-black leading-none mt-0.5">
                                                    {new Date(item.examDate).getDate()}
                                                </span>
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-800">{item.subject?.name}</h4>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-500 md:hidden">
                                                    <Calendar size={12} />
                                                    <span>{new Date(item.examDate).toLocaleDateString()}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Time */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center md:hidden">
                                                <Clock size={16} className="text-slate-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-semibold text-slate-700">{item.startTime} - {item.endTime}</p>
                                                <p className="text-[10px] text-slate-400 font-medium uppercase">Duration: {item.durationFormatted}</p>
                                            </div>
                                        </div>

                                        {/* Room */}
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-slate-50 flex items-center justify-center md:hidden">
                                                <MapPin size={16} className="text-slate-400" />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <MapPin size={14} className="text-pragyaOrange hidden md:block" />
                                                <span className="text-sm font-medium text-slate-600">{item.room || "TBA"}</span>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="flex items-center justify-between md:justify-start">
                                            {getStatusBadge(item.status)}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    )}
                </AnimatePresence>
            </main>

            {/* Hidden Admit Card Template for PDF Generation */}
            <div style={{ position: "absolute", top: "-10000px", left: "-10000px" }}>
                <AdmitCardTemplate
                    ref={admitCardRef}
                    data={{
                        student: student?.student,
                        class: student?.class,
                        // Fallback logic for rollNumber to ensure barcode generation
                        rollNumber: student?.uniqueRollNo,
                        examType: examTypes.find(t => t._id === selectedExamTypeId),
                        examRoutine: schedules.map(s => ({
                            subjectName: s.subject?.name,
                            subjectCode: s.subject?.code
                        })),
                        academicSession: academicSessions.find(s => s._id === currentSessionId),
                        serialNumber: "0210823"
                    }}
                    schoolInfo={schoolInfo}
                />
            </div>
        </div>
    );
};

export default ExamSchedule;
